import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

// The queries behind the Events list: the tab-strip counts and the paged rows.
// Both are scoped the same way: a super admin sees every event, a host only the
// events they own. Co-hosted events are merged in by the hook, not queried here.

class EventListAPI {
  /**
   * Aggregation counts for the tab strip: upcoming, completed, cancelled and
   * suggestions, plus hosts and requests for a super admin. A count that fails
   * is logged and left out rather than failing the rest.
   *
   * These are raw. The upcoming query is isCompleted==false, which cancelled
   * events also match, so the caller subtracts `cancelled` (GIS-231).
   */
  static async getTabCounts({ isSuperAdmin, vendorID }) {
    const eventsCol = collection(db, "events");
    const queries = isSuperAdmin
      ? {
        upcoming: query(eventsCol, where("isCompleted", "==", false)),
        completed: query(eventsCol, where("isCompleted", "==", true)),
        cancelled: query(eventsCol, where("status", "==", "cancelled")),
        suggestions: collection(db, "eventsSuggestions"),
        hosts: collection(db, "vendors"),
        requests: collection(db, "eventsRequests"),
      }
      : {
        upcoming: query(eventsCol, where("vendorID", "==", vendorID), where("isCompleted", "==", false)),
        completed: query(eventsCol, where("vendorID", "==", vendorID), where("isCompleted", "==", true)),
        cancelled: query(eventsCol, where("vendorID", "==", vendorID), where("status", "==", "cancelled")),
        suggestions: collection(db, "eventsSuggestions"),
      };

    const resolved = {};
    await Promise.all(
      Object.entries(queries).map(async ([key, q]) => {
        try {
          const snap = await getCountFromServer(q);
          resolved[key] = snap.data().count;
        } catch (error) {
          console.error(`Error fetching count for ${key}:`, error);
        }
      })
    );
    return resolved;
  }

  /**
   * One page of events for a tab ("upcoming", "completed" or "cancelled").
   *
   * `cursor` is the `lastDoc` a previous page returned, or null for the first
   * page. One row past `pageSize` is fetched only to learn whether another page
   * exists; it is never returned, and the cursor is the last row that is.
   */
  static async getEventsPage({ isSuperAdmin, vendorID, tab, cursor = null, pageSize }) {
    const isCompleted = tab === "completed";
    const isCancelled = tab === "cancelled";
    const dateOrder = isCompleted ? "desc" : "asc";

    // Cancelled events keep isCompleted:false, so they need their own
    // status=="cancelled" query (the isCompleted-based queries never see them).
    // No orderBy on that query so we don't need a new (status, date) composite
    // index — cancelled events are few and we sort by date client-side.
    const constraints = [];
    if (isSuperAdmin) {
      if (isCancelled) {
        constraints.push(where("status", "==", "cancelled"));
      } else {
        constraints.push(where("isCompleted", "==", isCompleted), orderBy("date", dateOrder));
      }
    } else if (isCancelled) {
      // Same shape as the super-admin cancelled branch, scoped to the host.
      // Two equality filters and no orderBy, so it needs no composite index
      // (the tab-count query above already runs this exact combination).
      constraints.push(
        where("vendorID", "==", vendorID),
        where("status", "==", "cancelled")
      );
    } else {
      // GIS-231. This used to be `where(vendorID) + orderBy(date, "asc")`
      // with NO tab predicate, so the oldest completed events came back
      // first and the client-side split threw the whole page away on the
      // upcoming tab. The observer then asked for page 2, 3, 4 — each one a
      // getDocs PLUS a getEventsRevenue callable, strictly serialized — so a
      // host with 40 past events paid four sequential double-hops before
      // seeing their first upcoming card.
      //
      // Needs the (vendorID, isCompleted, date) composite indexes added to
      // gist-firebase-functions/firestore.indexes.json in the same change.
      // Those must be DEPLOYED BEFORE this ships or every host query fails.
      constraints.push(
        where("vendorID", "==", vendorID),
        where("isCompleted", "==", isCompleted),
        orderBy("date", dateOrder)
      );
    }
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(pageSize + 1));

    const snapshot = await getDocs(query(collection(db, "events"), ...constraints));
    const docs = snapshot.docs;
    const displayed = docs.slice(0, pageSize);
    return {
      events: displayed.map((d) => ({ ...d.data(), id: d.id })),
      lastDoc: displayed.length > 0 ? displayed[displayed.length - 1] : null,
      hasMore: docs.length > pageSize,
    };
  }
}

export default EventListAPI;
